import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5bc01424"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "space-y-5 text-sm" }
const _hoisted_2 = {
  key: 0,
  class: "card"
}
const _hoisted_3 = { class: "info-grid" }
const _hoisted_4 = { class: "input-form-label text-gray-500" }
const _hoisted_5 = { class: "mt-1" }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = {
  key: 1,
  class: "card"
}
const _hoisted_8 = { class: "mb-5 flex items-center justify-between gap-4" }
const _hoisted_9 = /*#__PURE__*/_createTextVNode("PDF")
const _hoisted_10 = { class: "mt-2 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800" }
const _hoisted_11 = {
  key: 2,
  class: "card"
}
const _hoisted_12 = { class: "info-grid" }
const _hoisted_13 = { class: "md:col-span-2" }
const _hoisted_14 = { class: "input-form-label" }
const _hoisted_15 = { class: "mt-1 text-gray-800" }
const _hoisted_16 = { class: "mt-1 text-xs text-gray-500" }

import {
  computed,
  defineComponent,
  h,
  PropType,
  withDefaults,
} from "vue";
import Button from "@/components/common/Button.vue";
import StatusLabel from "@/components/common/StatusLabel.vue";
import { t } from "@/i18n";
import DocumentTemplateDataService from "@/services/documents/documentTemplate/DocumentTemplateDataService";
import FileDataService from "@/services/filemanager/files/FileDataService";
import { downloadPdf } from "@/utils/downloadPdf";
import { EEntityScope } from "@/types/common/entityApproval/EntityApproval";

interface TemplatePayload {
  id?: string | null;
  name?: string | null;
  template?: string | null;
  status?: string | null;
  comment?: string | null;
  approvalStatus?: string | null;
  viewScope?: string | null;
  editScope?: string | null;
  viewDirectorateIds?: string[] | null;
  editDirectorateIds?: string[] | null;
  coordinatorId?: string | null;
  coordinatorName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

type TemplatePayloadSection = "all" | "overview" | "template" | "access";


export default /*#__PURE__*/_defineComponent({
  props: {
    payload: null,
    section: { default: "all" }
  },
  setup(__props: any) {

const props = __props as { payload: Record<string, unknown> | TemplatePayload, section: TemplatePayloadSection }



const scopeEnum = EEntityScope;
const data = computed(() => props.payload as TemplatePayload);

const showSection = (section: Exclude<TemplatePayloadSection, "all">) => {
  return props.section === "all" || props.section === section;
};

const hasScope = computed(() => {
  return data.value.viewScope != null || data.value.editScope != null;
});

const scopeLabel = (scope?: string | null) => {
  const labels: Record<string, string> = {
    [EEntityScope.PRIVATE]: t("entityScope.private"),
    [EEntityScope.UNIT]: t("entityScope.unit"),
    [EEntityScope.MULTI_UNIT]: t("entityScope.multiUnit"),
    [EEntityScope.ALL]: t("entityScope.all"),
  };

  return scope ? labels[scope] ?? scope : "—";
};

const formatIds = (ids: string[] | null | undefined) => {
  return ids?.length ? ids.join(", ") : "—";
};

const entityStatusLabel = (status?: string | null) => {
  const labels: Record<string, string> = {
    WAITING: t("entityApproval.entityStatus.waiting"),
    ACTIVE: t("entityApproval.entityStatus.active"),
    DEACTIVATED: t("entityApproval.entityStatus.deactivated"),
  };
  return status ? labels[status] ?? "—" : "—";
};

const previewPdf = async () => {
  const convertResponse = await DocumentTemplateDataService.convert({
    name: data.value.name || "template",
    templateId: data.value.id,
    template:
      "<div style=\"font-family: 'times new roman', times, serif;\">" +
      (data.value.template || "") +
      "</div>",
    fieldConfig: [],
  });
  const fileGuid = convertResponse.data.data.fileGuid;
  const fileResponse = await FileDataService.downloadBlob(fileGuid);
  downloadPdf(fileResponse.data, data.value.name || "template");
};

const SectionTitle = defineComponent({
  props: {
    title: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    return () =>
      h(
        "h2",
        {
          class: "mb-5 text-lg font-semibold text-gray-800",
        },
        props.title
      );
  },
});

const InfoItem = defineComponent({
  props: {
    label: {
      type: String,
      required: true,
    },

    value: {
      type: [String, Number] as PropType<string | number>,
      default: "—",
    },
  },

  setup(props, { slots, attrs }) {
    return () =>
      h("div", attrs, [
        h(
          "span",
          {
            class: "input-form-label text-gray-500",
          },
          props.label
        ),

        h(
          "p",
          {
            class: "mt-1 break-words text-gray-800",
          },
          slots.default ? slots.default() : String(props.value || "—")
        ),
      ]);
  },
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showSection('overview'))
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createVNode(_unref(SectionTitle), {
            title: _unref(t)('entityApproval.template.mainInfo')
          }, null, 8, ["title"]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(InfoItem), {
              label: _unref(t)('common.title'),
              value: _unref(data).name || '—'
            }, null, 8, ["label", "value"]),
            _createVNode(_unref(InfoItem), {
              class: "md:col-span-2",
              label: _unref(t)('documentTemplate.comment'),
              value: _unref(data).comment || '—'
            }, null, 8, ["label", "value"]),
            _createElementVNode("div", null, [
              _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)("common.status")), 1),
              _createElementVNode("div", _hoisted_5, [
                (_unref(data).status)
                  ? (_openBlock(), _createBlock(StatusLabel, {
                      key: 0,
                      label: String(_unref(data).status)
                    }, null, 8, ["label"]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_6, "—"))
              ])
            ]),
            _createVNode(_unref(InfoItem), {
              label: _unref(t)('entityApproval.approvalStatus'),
              value: entityStatusLabel(_unref(data).approvalStatus)
            }, null, 8, ["label", "value"]),
            _createVNode(_unref(InfoItem), {
              label: _unref(t)('common.createdAt'),
              value: _unref(data).createdAt ? String(_unref(data).createdAt) : '—'
            }, null, 8, ["label", "value"]),
            _createVNode(_unref(InfoItem), {
              label: _unref(t)('common.updatedAt'),
              value: _unref(data).updatedAt ? String(_unref(data).updatedAt) : '—'
            }, null, 8, ["label", "value"])
          ])
        ]))
      : _createCommentVNode("", true),
    (showSection('template'))
      ? (_openBlock(), _createElementBlock("section", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_unref(SectionTitle), {
              class: "mb-0",
              title: _unref(t)('documentTemplate.template')
            }, null, 8, ["title"]),
            _createVNode(Button, {
              type: "button",
              onClick: previewPdf
            }, {
              default: _withCtx(() => [
                _hoisted_9
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("code", null, _toDisplayString(_unref(data).template || "—"), 1)
          ])
        ]))
      : _createCommentVNode("", true),
    (showSection('access') && _unref(hasScope))
      ? (_openBlock(), _createElementBlock("section", _hoisted_11, [
          _createVNode(_unref(SectionTitle), {
            title: _unref(t)('entityScope.title')
          }, null, 8, ["title"]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_unref(InfoItem), {
              label: _unref(t)('entityScope.viewScope'),
              value: scopeLabel(_unref(data).viewScope)
            }, null, 8, ["label", "value"]),
            _createVNode(_unref(InfoItem), {
              label: _unref(t)('entityScope.editScope'),
              value: scopeLabel(_unref(data).editScope)
            }, null, 8, ["label", "value"]),
            (_unref(data).viewScope === _unref(scopeEnum).MULTI_UNIT)
              ? (_openBlock(), _createBlock(_unref(InfoItem), {
                  key: 0,
                  label: _unref(t)('entityScope.viewDirectorates'),
                  value: formatIds(_unref(data).viewDirectorateIds)
                }, null, 8, ["label", "value"]))
              : _createCommentVNode("", true),
            (_unref(data).editScope === _unref(scopeEnum).MULTI_UNIT)
              ? (_openBlock(), _createBlock(_unref(InfoItem), {
                  key: 1,
                  label: _unref(t)('entityScope.editDirectorates'),
                  value: formatIds(_unref(data).editDirectorateIds)
                }, null, 8, ["label", "value"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(t)("entityScope.coordinator")), 1),
              _createElementVNode("p", _hoisted_15, _toDisplayString(_unref(data).coordinatorName ||
              (_unref(data).coordinatorId != null
                ? _unref(data).coordinatorId
                : _unref(t)("entityScope.coordinatorAny"))), 1),
              _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(t)("entityScope.coordinatorHint")), 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})